import { IPolish } from './polish';
import { TokenType } from './tokenizer';

export class ExpNode {
  public readonly op: number | string;
  public readonly left: ExpNode | null;
  public readonly right: ExpNode | null = null;
  public readonly literal?: string;

  constructor(op: number | string, left: ExpNode | null, right: ExpNode | null = null, literal?: string) {
    this.op = op;
    this.left = left;
    this.right = right;
    this.literal = literal;
  }

  isLeaf() {
    return this.op === TokenType.LEAF;
  }

  isAtomic() {
    return this.isLeaf() || (this.op === TokenType.NOT && this.left?.isLeaf());
  }

  getLiteralValue() {
    return this.literal;
  }

  static CreateEquals(left: ExpNode | null, right: ExpNode | null) {
    return new ExpNode(TokenType.EQUALS, left, right);
  }

  static CreateAnd(left: ExpNode | null, right: ExpNode | null) {
    return new ExpNode(TokenType.AND, left, right);
  }

  static CreateNot(exp: ExpNode | null) {
    return new ExpNode(TokenType.NOT, exp);
  }

  static CreateOr(left: ExpNode | null, right: ExpNode | null) {
    return new ExpNode(TokenType.OR, left, right);
  }

  static CreateLiteral(lit: string) {
    return new ExpNode(TokenType.LEAF, null, null, lit);
  }
}

export function make(gen: Generator<IPolish, IPolish>): ExpNode | null {
  const data = gen.next().value;

  switch (data.type) {
    case TokenType.LITERAL:
      return ExpNode.CreateLiteral(data.value);
    case TokenType.NOT:
      return ExpNode.CreateNot(make(gen));
    case TokenType.OR: {
      const left = make(gen);
      const right = make(gen);
      return ExpNode.CreateOr(left, right);
    }
    case TokenType.AND: {
      const left = make(gen);
      const right = make(gen);
      return ExpNode.CreateAnd(left, right);
    }
    case TokenType.EQUALS: {
      const left = make(gen);
      const right = make(gen);
      return ExpNode.CreateEquals(left, right);
    }
  }
  return null;
}

export function nodeEvaluator(tree: ExpNode | null, literalResolver: any): any {
  if (tree != null) {
    if (tree.isLeaf()) {
      return literalResolver(tree.getLiteralValue());
    }

    if (tree.op === TokenType.NOT) {
      return !nodeEvaluator(tree.left, literalResolver);
    }

    if (tree.op === TokenType.OR) {
      return nodeEvaluator(tree.left, literalResolver) || nodeEvaluator(tree.right, literalResolver);
    }

    if (tree.op === TokenType.AND) {
      return nodeEvaluator(tree.left, literalResolver) && nodeEvaluator(tree.right, literalResolver);
    }

    if (tree.op === TokenType.EQUALS) {
      return nodeEvaluator(tree.left, literalResolver) === nodeEvaluator(tree.right, literalResolver);
    }
  }
}
